"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { SectionHeader } from "fansunited-sports-ui";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { CalendarCheck, Clock, Trophy, Equal, Home, Plane } from "lucide-react";
import { SoccerBallIcon } from "@/components/ui/icons/PlayerEventIcons";
import { TeamLogo } from "@/components/ui/TeamLogo";
import type { CompetitionStats, GoalDistribution } from "fansunited-data-layer";
import type { TeamWithWins } from "@/utilities/data/competition/derive/types";
export interface SeasonStatsCardsProps {
  competitionStats: CompetitionStats;
  goalDistribution?: GoalDistribution;
  teamWithMostWins?: TeamWithWins | null;
}

/** Stat item with icon, label, and value */
const StatItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  className?: string;
}> = t0 => {
  const $ = _c(13);
  const {
    icon,
    label,
    value,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const t2 = `flex items-center justify-between gap-2 px-3 py-2.5 sm:px-4 sm:py-3 ${className}`;
  let t3;
  if ($[0] !== icon) {
    t3 = <span className="text-muted-foreground shrink-0">{icon}</span>;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== label) {
    t4 = <span className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-card-foreground truncate">{label}</span>;
    $[2] = label;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">{t3}{t4}</div>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== value) {
    t6 = <span className="text-xl sm:text-2xl font-bold text-accent shrink-0">{value}</span>;
    $[7] = value;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t2 || $[10] !== t5 || $[11] !== t6) {
    t7 = <div className={t2}>{t5}{t6}</div>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};

/**
 * SeasonStatsCards - Season statistics section with row-based layout similar to sports stats cards.
 */
export const SeasonStatsCards: React.FC<SeasonStatsCardsProps> = t0 => {
  const $ = _c(114);
  const {
    competitionStats,
    goalDistribution,
    teamWithMostWins
  } = t0;
  const labels = useLabels();
  const nav = useNavigation();
  const matchesToPlay = competitionStats.totalMatches - competitionStats.finishedMatches;
  const totalWins = competitionStats.homeWins + competitionStats.awayWins;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      typography: {
        name: "text-sm uppercase font-bold text-foreground"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== labels.homePage.seasonStats) {
    t2 = {
      name: labels.homePage.seasonStats,
      styles: t1
    };
    $[1] = labels.homePage.seasonStats;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      container: "mb-4"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <SectionHeader entity={t2} styles={t3} />;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CalendarCheck className="w-4 h-4" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== competitionStats.finishedMatches || $[8] !== labels.homePage.matchesPlayed) {
    t6 = <StatItem icon={t5} label={labels.homePage.matchesPlayed} value={competitionStats.finishedMatches} />;
    $[7] = competitionStats.finishedMatches;
    $[8] = labels.homePage.matchesPlayed;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Clock className="w-4 h-4" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== labels.homePage.toPlay || $[12] !== matchesToPlay) {
    t8 = <StatItem icon={t7} label={labels.homePage.toPlay} value={matchesToPlay} />;
    $[11] = labels.homePage.toPlay;
    $[12] = matchesToPlay;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Trophy className="w-4 h-4" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== labels.homePage.totalWins || $[16] !== totalWins) {
    t10 = <StatItem icon={t9} label={labels.homePage.totalWins} value={totalWins} />;
    $[15] = labels.homePage.totalWins;
    $[16] = totalWins;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Equal className="w-4 h-4" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== competitionStats.draws || $[20] !== labels.homePage.draws) {
    t12 = <StatItem icon={t11} label={labels.homePage.draws} value={competitionStats.draws} />;
    $[19] = competitionStats.draws;
    $[20] = labels.homePage.draws;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== labels.homePage.mostWins || $[23] !== nav || $[24] !== teamWithMostWins) {
    t13 = teamWithMostWins && <Link prefetch={false} href={teamWithMostWins.competitor.fansUnitedTeamId ? nav.team(teamWithMostWins.competitor.fansUnitedTeamId) : "#"} className="flex flex-col gap-1 px-3 py-2.5 hover:bg-muted-foreground/5 transition-colors"><div className="flex items-center gap-1.5"><Trophy className="w-4 h-4 text-muted-foreground shrink-0" /><span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{labels.homePage.mostWins}</span></div><div className="flex items-center justify-between"><div className="flex items-center gap-1.5 min-w-0"><TeamLogo src={teamWithMostWins.competitor.logo} size="xs" /><span className="text-xs font-semibold uppercase tracking-wide text-card-foreground truncate">{teamWithMostWins.competitor.name}</span></div><span className="text-xl font-bold text-accent shrink-0 ml-2">{teamWithMostWins.stats?.wins ?? 0}</span></div></Link>;
    $[22] = labels.homePage.mostWins;
    $[23] = nav;
    $[24] = teamWithMostWins;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SoccerBallIcon className="w-4 h-4" />;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const t15 = goalDistribution?.totalGoals ?? "\u2014";
  let t16;
  if ($[27] !== labels.homePage.totalGoals || $[28] !== t15) {
    t16 = <StatItem icon={t14} label={labels.homePage.totalGoals} value={t15} />;
    $[27] = labels.homePage.totalGoals;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <SoccerBallIcon className="w-4 h-4" />;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = labels.homePage.goalsPerMatch;
  let t19;
  if ($[31] !== competitionStats.goalsPerMatch) {
    t19 = competitionStats.goalsPerMatch.toFixed(1);
    $[31] = competitionStats.goalsPerMatch;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== labels.homePage.goalsPerMatch || $[34] !== t19) {
    t20 = <StatItem icon={t17} label={t18} value={t19} />;
    $[33] = labels.homePage.goalsPerMatch;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Home className="w-4 h-4" />;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== competitionStats.homeWins || $[38] !== labels.homePage.homeWins) {
    t22 = <StatItem icon={t21} label={labels.homePage.homeWins} value={competitionStats.homeWins} />;
    $[37] = competitionStats.homeWins;
    $[38] = labels.homePage.homeWins;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Plane className="w-4 h-4" />;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== competitionStats.awayWins || $[42] !== labels.homePage.awayWins) {
    t24 = <StatItem icon={t23} label={labels.homePage.awayWins} value={competitionStats.awayWins} />;
    $[41] = competitionStats.awayWins;
    $[42] = labels.homePage.awayWins;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t10 || $[45] !== t12 || $[46] !== t13 || $[47] !== t16 || $[48] !== t20 || $[49] !== t22 || $[50] !== t24 || $[51] !== t6 || $[52] !== t8) {
    t25 = <div className="flex flex-col sm:hidden bg-muted rounded-lg overflow-hidden divide-y divide-background">{t6}{t8}{t10}{t12}{t13}{t16}{t20}{t22}{t24}</div>;
    $[44] = t10;
    $[45] = t12;
    $[46] = t13;
    $[47] = t16;
    $[48] = t20;
    $[49] = t22;
    $[50] = t24;
    $[51] = t6;
    $[52] = t8;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <CalendarCheck className="w-4 h-4" />;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== competitionStats.finishedMatches || $[56] !== labels.homePage.matchesPlayed) {
    t27 = <StatItem icon={t26} label={labels.homePage.matchesPlayed} value={competitionStats.finishedMatches} />;
    $[55] = competitionStats.finishedMatches;
    $[56] = labels.homePage.matchesPlayed;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Clock className="w-4 h-4" />;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== labels.homePage.toPlay || $[60] !== matchesToPlay) {
    t29 = <StatItem icon={t28} label={labels.homePage.toPlay} value={matchesToPlay} className="border-l border-background" />;
    $[59] = labels.homePage.toPlay;
    $[60] = matchesToPlay;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t27 || $[63] !== t29) {
    t30 = <div className="grid grid-cols-2 bg-muted rounded-lg overflow-hidden">{t27}{t29}</div>;
    $[62] = t27;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Trophy className="w-4 h-4" />;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== labels.homePage.totalWins || $[67] !== totalWins) {
    t32 = <StatItem icon={t31} label={labels.homePage.totalWins} value={totalWins} />;
    $[66] = labels.homePage.totalWins;
    $[67] = totalWins;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== labels.homePage.mostWins || $[70] !== nav || $[71] !== teamWithMostWins) {
    t33 = teamWithMostWins ? <Link prefetch={false} href={teamWithMostWins.competitor.fansUnitedTeamId ? nav.team(teamWithMostWins.competitor.fansUnitedTeamId) : "#"} className="flex items-center justify-between px-4 py-3 border-l border-background hover:bg-muted-foreground/5 transition-colors"><div className="flex items-center gap-2 min-w-0"><Trophy className="w-4 h-4 text-muted-foreground shrink-0" /><span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground shrink-0">{labels.homePage.mostWins}:</span><TeamLogo src={teamWithMostWins.competitor.logo} size="sm" /><span className="text-xs font-semibold uppercase tracking-wide text-card-foreground truncate">{teamWithMostWins.competitor.name}</span></div><span className="text-2xl font-bold text-accent shrink-0 ml-2">{teamWithMostWins.stats?.wins ?? 0}</span></Link> : <div className="border-l border-background" />;
    $[69] = labels.homePage.mostWins;
    $[70] = nav;
    $[71] = teamWithMostWins;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Equal className="w-4 h-4" />;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  let t35;
  if ($[74] !== competitionStats.draws || $[75] !== labels.homePage.draws) {
    t35 = <StatItem icon={t34} label={labels.homePage.draws} value={competitionStats.draws} className="border-l border-background" />;
    $[74] = competitionStats.draws;
    $[75] = labels.homePage.draws;
    $[76] = t35;
  } else {
    t35 = $[76];
  }
  let t36;
  if ($[77] !== t32 || $[78] !== t33 || $[79] !== t35) {
    t36 = <div className="grid grid-cols-3 bg-muted rounded-lg overflow-hidden">{t32}{t33}{t35}</div>;
    $[77] = t32;
    $[78] = t33;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <SoccerBallIcon className="w-4 h-4" />;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  const t38 = goalDistribution?.totalGoals ?? "\u2014";
  let t39;
  if ($[82] !== labels.homePage.totalGoals || $[83] !== t38) {
    t39 = <StatItem icon={t37} label={labels.homePage.totalGoals} value={t38} />;
    $[82] = labels.homePage.totalGoals;
    $[83] = t38;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  let t40;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <SoccerBallIcon className="w-4 h-4" />;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  const t41 = labels.homePage.goalsPerMatch;
  let t42;
  if ($[86] !== competitionStats.goalsPerMatch) {
    t42 = competitionStats.goalsPerMatch.toFixed(1);
    $[86] = competitionStats.goalsPerMatch;
    $[87] = t42;
  } else {
    t42 = $[87];
  }
  let t43;
  if ($[88] !== labels.homePage.goalsPerMatch || $[89] !== t42) {
    t43 = <StatItem icon={t40} label={t41} value={t42} className="border-l border-background" />;
    $[88] = labels.homePage.goalsPerMatch;
    $[89] = t42;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Home className="w-4 h-4" />;
    $[91] = t44;
  } else {
    t44 = $[91];
  }
  let t45;
  if ($[92] !== competitionStats.homeWins || $[93] !== labels.homePage.homeWins) {
    t45 = <StatItem icon={t44} label={labels.homePage.homeWins} value={competitionStats.homeWins} className="border-l border-background" />;
    $[92] = competitionStats.homeWins;
    $[93] = labels.homePage.homeWins;
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <Plane className="w-4 h-4" />;
    $[95] = t46;
  } else {
    t46 = $[95];
  }
  let t47;
  if ($[96] !== competitionStats.awayWins || $[97] !== labels.homePage.awayWins) {
    t47 = <StatItem icon={t46} label={labels.homePage.awayWins} value={competitionStats.awayWins} className="border-l border-background" />;
    $[96] = competitionStats.awayWins;
    $[97] = labels.homePage.awayWins;
    $[98] = t47;
  } else {
    t47 = $[98];
  }
  let t48;
  if ($[99] !== t39 || $[100] !== t43 || $[101] !== t45 || $[102] !== t47) {
    t48 = <div className="grid grid-cols-4 bg-muted rounded-lg overflow-hidden">{t39}{t43}{t45}{t47}</div>;
    $[99] = t39;
    $[100] = t43;
    $[101] = t45;
    $[102] = t47;
    $[103] = t48;
  } else {
    t48 = $[103];
  }
  let t49;
  if ($[104] !== t30 || $[105] !== t36 || $[106] !== t48) {
    t49 = <div className="hidden sm:block space-y-1">{t30}{t36}{t48}</div>;
    $[104] = t30;
    $[105] = t36;
    $[106] = t48;
    $[107] = t49;
  } else {
    t49 = $[107];
  }
  let t50;
  if ($[108] !== t25 || $[109] !== t49) {
    t50 = <div className="space-y-1">{t25}{t49}</div>;
    $[108] = t25;
    $[109] = t49;
    $[110] = t50;
  } else {
    t50 = $[110];
  }
  let t51;
  if ($[111] !== t4 || $[112] !== t50) {
    t51 = <section>{t4}{t50}</section>;
    $[111] = t4;
    $[112] = t50;
    $[113] = t51;
  } else {
    t51 = $[113];
  }
  return t51;
};